import axios from "axios";
import { useEffect, useState } from "react";
import {
  Linkedin,
  Loader2,
  RefreshCw,
  UserPlus,
  X,
  Sparkles,
  MapPin,
  ExternalLink,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

/**
 * Auto-sourced LinkedIn prospects for a job.
 * - Fires when the job is created (server-side background task)
 * - Recruiter can "Buscar mais 15" to fetch additional prospects
 * - "Adicionar ao funil" promotes prospect → candidate + sends invite InMail
 * - "Dispensar" hides it (kept in DB to avoid re-sourcing)
 *
 * Lives inside JobDetailPage, below the lead poller panel.
 */
const JobLinkedInProspectsPanel = ({ job }) => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchingMore, setSearchingMore] = useState(false);
  const [inviting, setInviting] = useState({});   // { prospectId: true } – sending invite
  const [promoting, setPromoting] = useState({}); // { prospectId: true } – adding to funnel
  const [dismissing, setDismissing] = useState({});

  const loadProspects = async () => {
    try {
      const res = await axios.get(
        `${API}/jobs/${job.id}/linkedin/prospects?status_filter=new`,
      );
      setProspects(res.data.prospects || []);
    } catch {
      // Best-effort
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProspects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const handleSearchMore = async () => {
    setSearchingMore(true);
    try {
      const res = await axios.post(
        `${API}/jobs/${job.id}/linkedin/prospects/search-more`,
      );
      const saved = res.data.saved || 0;
      const dup = res.data.duplicates_skipped || 0;
      if (saved > 0) {
        toast.success(
          `${saved} prospects novos adicionados${
            dup > 0 ? ` (${dup} duplicados ignorados)` : ""
          }.`,
        );
        loadProspects();
      } else if (dup > 0) {
        toast.info(
          `Nenhum novo. ${dup} já estavam na lista ou no funil. Tente em outro momento.`,
        );
      } else {
        toast.info("Nenhum perfil encontrado pra essa vaga.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao buscar mais.");
    } finally {
      setSearchingMore(false);
    }
  };

  const handleInvite = async (prospect) => {
    setInviting((p) => ({ ...p, [prospect.id]: true }));
    try {
      const res = await axios.post(
        `${API}/jobs/${job.id}/linkedin/prospects/${prospect.id}/invite`,
      );
      if (res.data.already_in_funnel) {
        toast.info("Já está no funil.");
      } else {
        toast.success(
          `Convite enviado para ${prospect.name}! Quando aceito, a Maya inicia o contato pelo LinkedIn.`,
        );
      }
      setProspects((cur) => cur.filter((p) => p.id !== prospect.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar convite.");
    } finally {
      setInviting((p) => {
        const next = { ...p };
        delete next[prospect.id];
        return next;
      });
    }
  };

  const handlePromote = async (prospect) => {
    setPromoting((p) => ({ ...p, [prospect.id]: true }));
    try {
      const res = await axios.post(
        `${API}/jobs/${job.id}/linkedin/prospects/${prospect.id}/promote`,
        { send_inmail: false },
      );
      if (res.data.already_in_funnel) {
        toast.info("Já está no funil.");
      } else {
        toast.success(
          `${prospect.name} adicionado(a) ao funil. CV e score sendo gerados em background.`,
        );
      }
      setProspects((cur) => cur.filter((p) => p.id !== prospect.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao adicionar ao funil.");
    } finally {
      setPromoting((p) => {
        const next = { ...p };
        delete next[prospect.id];
        return next;
      });
    }
  };

  const handleDismiss = async (prospect) => {
    setDismissing((p) => ({ ...p, [prospect.id]: true }));
    try {
      await axios.post(
        `${API}/jobs/${job.id}/linkedin/prospects/${prospect.id}/dismiss`,
      );
      setProspects((cur) => cur.filter((p) => p.id !== prospect.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao dispensar.");
    } finally {
      setDismissing((p) => {
        const next = { ...p };
        delete next[prospect.id];
        return next;
      });
    }
  };

  return (
    <section
      data-testid="job-linkedin-prospects-panel"
      className="bg-white border border-slate-200 rounded-lg p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div className="flex items-start gap-2">
          <div className="w-9 h-9 rounded-md bg-violet-50 text-violet-700 flex items-center justify-center shrink-0">
            <Search className="w-4 h-4" />
          </div>
          <div>
            <h2 className="heading text-base font-semibold text-slate-900 leading-tight">
              Prospects do LinkedIn{" "}
              <span className="text-xs font-normal text-slate-500">
                ({prospects.length})
              </span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Perfis encontrados automaticamente que dão match com esta vaga.
              Envie convite de conexão com 1 clique. A Maya inicia o contato após aceite.
            </p>
          </div>
        </div>
        <Button
          data-testid="prospects-search-more"
          size="sm"
          variant="outline"
          onClick={handleSearchMore}
          disabled={searchingMore}
          className="text-violet-700 border-violet-200 hover:bg-violet-50"
        >
          {searchingMore ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          Buscar mais 15
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-1" />
          Carregando…
        </div>
      ) : prospects.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-6 text-center">
          <Linkedin className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-xs text-slate-600 max-w-md mx-auto leading-relaxed">
            Nenhum prospect ativo agora. A busca automática roda no momento da
            criação da vaga. Clique <strong>"Buscar mais 15"</strong> para
            puxar mais perfis usando palavras-chave do título e skills da vaga.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {prospects.map((p) => (
            <div
              key={p.id}
              data-testid={`prospect-${p.id}`}
              className="bg-slate-50/50 border border-slate-200 rounded-lg p-3 hover:border-slate-300 transition-colors"
            >
              <div className="flex items-start gap-2.5 mb-2.5">
                {p.picture_url ? (
                  <img
                    src={p.picture_url}
                    alt={p.name}
                    className="w-10 h-10 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-blue-50 text-brand-blue flex items-center justify-center shrink-0">
                    <Linkedin className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-slate-900 leading-tight">
                    {p.name}
                  </h3>
                  {p.headline && (
                    <p className="text-[11px] text-slate-600 line-clamp-2 mt-0.5">
                      {p.headline}
                    </p>
                  )}
                  {p.location && (
                    <div className="text-[10px] text-slate-500 inline-flex items-center gap-1 mt-1">
                      <MapPin className="w-2.5 h-2.5" />
                      {p.location}
                    </div>
                  )}
                </div>
                {p.profile_url && (
                  <a
                    href={p.profile_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-400 hover:text-brand-blue shrink-0"
                    title="Abrir no LinkedIn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              <div className="flex flex-col gap-1 pt-2 border-t border-slate-100">
                {/* Row 1: Adicionar ao Funil (full width, generates CV + score) */}
                <Button
                  data-testid={`prospect-promote-${p.id}`}
                  size="sm"
                  onClick={() => handlePromote(p)}
                  disabled={!!promoting[p.id] || !!inviting[p.id]}
                  className="w-full h-7 text-[11px] bg-brand-blue hover:bg-brand-blue-dark text-white"
                >
                  {promoting[p.id] ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="w-3 h-3 mr-1" />
                      <Sparkles className="w-2.5 h-2.5 mr-0.5" />
                    </>
                  )}
                  Adicionar ao Funil
                </Button>
                {/* Row 2: Enviar Convite + Dispensar */}
                <div className="flex gap-1">
                  <Button
                    data-testid={`prospect-invite-${p.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => handleInvite(p)}
                    disabled={!!inviting[p.id] || !!promoting[p.id]}
                    className="flex-1 h-7 text-[11px] border-brand-blue text-brand-blue hover:bg-blue-50"
                  >
                    {inviting[p.id] ? (
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    ) : (
                      <Linkedin className="w-3 h-3 mr-1" />
                    )}
                    Enviar Convite
                  </Button>
                  <Button
                    data-testid={`prospect-dismiss-${p.id}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDismiss(p)}
                    disabled={!!dismissing[p.id]}
                    className="h-7 text-[11px] text-slate-500 hover:text-rose-600"
                    title="Dispensar"
                  >
                    {dismissing[p.id] ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <X className="w-3.5 h-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default JobLinkedInProspectsPanel;
